// Where the app is, read from the navigation container the app already owns
// (src/services/navigationService.ts). The shell uses it to decide between
// the logged-out frame (full-bleed brand ground) and the app frame (sidebar),
// and to highlight the active sidebar item. Nothing here navigates.

import { useEffect, useState } from 'react';

import { lockNow } from '../../../src/services/auth/idleLock';
import { isVaultReadyToUse } from '../../../src/services/encryption/MasterKeyService';
import { navigationRef } from '../../../src/services/navigationService';

export interface RootRoute {
  /** Name of the root stack route, e.g. 'Auth', 'Dashboard', 'Documents'. */
  root: string | undefined;
  /** Name of the nested route inside it, e.g. 'AllTasks', 'Home'. */
  nested: string | undefined;
}

// Roots that render without the app chrome.
const NO_CHROME_ROOTS = new Set([
  'Auth',
  'AcceptInvitation',
  'EmailVerificationResult',
  'VaultMigration',
  'OnboardingWizard',
]);

export function isAppRoot(root: string | undefined): boolean {
  return !!root && !NO_CHROME_ROOTS.has(root);
}

function readRoute(): RootRoute {
  try {
    if (!navigationRef.isReady()) return { root: undefined, nested: undefined };
    const state = navigationRef.getRootState();
    const route = state?.routes?.[state.index ?? 0];
    const inner = route?.state;
    const nested = inner?.routes?.[inner.index ?? 0]?.name;
    return { root: route?.name, nested };
  } catch {
    return { root: undefined, nested: undefined };
  }
}

export function useRootRoute(): RootRoute {
  const [route, setRoute] = useState<RootRoute>(readRoute);

  // Dev-only: lets the navigation container be inspected from the browser
  // console (`__fkNav.getRootState()`) when a frame decision looks wrong.
  if (__DEV__ && typeof window !== 'undefined') {
    const w = window as unknown as {
      __fkNav?: unknown;
      __fkRoute?: RootRoute;
      __fkLockNow?: () => void;
      __fkVaultReady?: () => Promise<boolean>;
    };
    w.__fkNav = navigationRef;
    w.__fkRoute = route;
    w.__fkLockNow = lockNow;
    w.__fkVaultReady = isVaultReadyToUse;
  }

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let poll: ReturnType<typeof setInterval> | undefined;

    const update = () => {
      const next = readRoute();
      setRoute((prev) => (prev.root === next.root && prev.nested === next.nested ? prev : next));
    };

    const attach = () => {
      if (!navigationRef.isReady()) return false;
      update();
      unsubscribe = navigationRef.addListener('state', update);
      return true;
    };

    if (!attach()) {
      // The container mounts inside App; poll briefly until it is ready.
      poll = setInterval(() => {
        if (attach() && poll) clearInterval(poll);
      }, 150);
    }

    // The container's 'state' event has been observed to miss some nested
    // navigations on web; a light poll keeps the frame and the sidebar
    // highlight honest. setRoute bails when nothing changed.
    const watchdog = setInterval(update, 400);

    return () => {
      unsubscribe?.();
      if (poll) clearInterval(poll);
      clearInterval(watchdog);
    };
  }, []);

  return route;
}
